import { defaultCardStyle } from "components/ui/card";
import React from "react";
import { createPortal } from "react-dom";
import { ResponsiveMode } from "store/reducers/responsive";
import { useResponsiveMode } from "store/selectors/hooks";
import { useNavbarPortal } from "./navbar-portal-context";

interface ContentProps {
    children?: React.ReactNode;
    className?: string;
}

export const Content: React.FC<ContentProps> = props => (
    <ContentContainer className={props.className}>
        <ContentFull>{props.children}</ContentFull>
    </ContentContainer>
);

interface ContentContainerProps {
    className?: string;
    children?: React.ReactNode;
}

export const ContentContainer: React.FC<ContentContainerProps> = props => (
    <div
        className={`mx-0 gap-1 md:mx-2 md:gap-2 lg:mx-4 lg:gap-4 ${
            props.className ?? ""
        }`}>
        {props.children}
    </div>
);

interface ContentFullProps {
    children?: React.ReactNode;
    className?: string;
}

export const ContentFull: React.FC<ContentFullProps> = props => {
    return (
        <div className={`w-full p-2 lg:p-4 ${defaultCardStyle} ${props.className ?? ""}`}>
            {props.children}
        </div>
    );
};

interface TilesProps {
    children?: React.ReactNode;
}

export const Tiles: React.FC<TilesProps> = props => (
    <div className="grid grid-cols-1 w-full gap-0 md:gap-2 md:grid-cols-2 lg:grid-cols-3 lg:gap-4">
        {props.children}
    </div>
);

interface TileProps {
    children?: React.ReactNode;
}

export const Tile: React.FC<TileProps> = props => (
    <div className="bg-white p-2 md:rounded-sm lg:p-4 lg:rounded rounded shadow">
        {props.children}
    </div>
);

interface StageProps {
    children?: React.ReactNode;
}

export const Stage: React.FC<StageProps> = props => {
    return <>{props.children}</>;
};

interface SectionHeaderProps {
    title: string;
    children?: React.ReactNode;
    stackBreakpoint?: "md" | "lg";
}

export const PageHeader: React.FC<SectionHeaderProps> = props => {
    const responsiveMode = useResponsiveMode();
    const { portalTarget } = useNavbarPortal();
    const isMobile =
        responsiveMode === ResponsiveMode.small ||
        responsiveMode === ResponsiveMode.medium;

    const flexClass =
        props.stackBreakpoint === "md"
            ? "px-4 py-2 lg:py-4 md:flex md:items-center md:justify-between"
            : props.stackBreakpoint === "lg"
              ? "px-4 py-2 lg:py-4 lg:flex lg:items-center lg:justify-between"
              : "px-4 py-2 lg:py-4 flex items-center justify-between";

    const content = (
        <div className={flexClass}>
            <h2 className="text-xl/8 text-zinc-950 sm:text-xl/8 twoverride">
                {props.title}
            </h2>
            <div>{props.children}</div>
        </div>
    );

    if (isMobile && portalTarget) {
        return createPortal(content, portalTarget);
    }

    return content;
};

export const SectionHeader: React.FC<SectionHeaderProps> = props => {
    return (
        <div className="px-4 py-2 lg:py-4 lg:flex items-end justify-between">
            <h2 className="text-2xl/8 font-semibold text-zinc-950 sm:text-xl/8">
                {props.title}
            </h2>
            <div>{props.children}</div>
        </div>
    );
};

interface ContentHeaderProps {
    children?: React.ReactNode;
}

export const ContentHeader: React.FC<ContentHeaderProps> = props => (
    <h2 className="text-xl">{props.children}</h2>
);

interface ContentSubheaderProps {
    children?: React.ReactNode;
}

export const ContentSubheader: React.FC<ContentSubheaderProps> = props => (
    <h6 className="text-gray-500 text-sm -mt-2 mb-4">{props.children}</h6>
);

interface ContentSection2UpProps {
    children?: React.ReactNode;
    className?: string;
}

export const ContentSection2Up: React.FC<ContentSection2UpProps> = props => (
    <div
        className={`mx-0 max-md:space-y-4 md:flex md:mx-2 md:pb-2 md:gap-4 ${
            props.className ?? ""
        }`}>
        {props.children}
    </div>
);

export const ContentSection2UpLg: React.FC<ContentSection2UpProps> = props => (
    <div
        className={`mx-0 gap-1 lg:flex lg:mx-2 lg:pb-2 lg:gap-4 ${
            props.className ?? ""
        }`}>
        {props.children}
    </div>
);

export const ContentSectionColumn: React.FC<
    React.HTMLAttributes<HTMLDivElement>
> = props => {
    const { className, ...rest } = props;
    const classNameActual = `${className ?? ""} flex-1`;
    return (
        <div {...rest} className={classNameActual}>
            {props.children}
        </div>
    );
};

interface EntityPageHeaderProps {
    children?: React.ReactNode;
}

export const EntityPageHeader: React.FC<EntityPageHeaderProps> = props => {
    const responsiveMode = useResponsiveMode();
    const { portalTarget } = useNavbarPortal();
    const isMobile =
        responsiveMode === ResponsiveMode.small ||
        responsiveMode === ResponsiveMode.medium;

    const content = (
        <div className="flex items-start flex-wrap justify-between sm:px-4 sm:pt-4 sm:pb-2">
            {props.children}
        </div>
    );

    if (isMobile && portalTarget) {
        return createPortal(content, portalTarget);
    }

    return content;
};
